import { useState, useEffect, useCallback } from 'react';

/**
 * useNotifications - Hook for browser notifications
 *
 * Provides:
 * - Permission request
 * - Show notification
 * - Permission status tracking
 */

export function useNotifications() {
  const [permission, setPermission] = useState('default');

  // Check initial permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Show a notification
  const showNotification = useCallback((title, options = {}) => {
    if (!('Notification' in window)) {
      return null;
    }

    if (Notification.permission !== 'granted') {
      return null;
    }

    // Don't show if window is focused
    if (document.hasFocus()) {
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: options.tag || 'chat-message',
        renotify: true,
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }, []);

  // Show chat message notification
  const showMessageNotification = useCallback((senderName, messageText, chatId, onClick) => {
    const truncatedMessage = messageText.length > 50
      ? messageText.substring(0, 50) + '...'
      : messageText;

    const notification = showNotification(senderName, {
      body: truncatedMessage,
      tag: `chat-${chatId}`,
      data: { chatId }
    });

    if (notification && onClick) {
      notification.onclick = () => {
        window.focus();
        onClick(chatId);
        notification.close();
      };
    }

    return notification;
  }, [showNotification]);

  return {
    permission,
    isSupported: 'Notification' in window,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    requestPermission,
    showNotification,
    showMessageNotification
  };
}

export default useNotifications;
